import { createContext, useContext, useEffect, useMemo, useState } from "react";
import {
  clearStoredApiKey,
  getStoredApiKey,
  setStoredApiKey,
  syncApiKeyFromUrl,
} from "../lib/apiKey";

type ApiKeyContextValue = {
  apiKey: string | null;
  setApiKey: (key: string) => void;
  clearApiKey: () => void;
  initialized: boolean;
};

const ApiKeyContext = createContext<ApiKeyContextValue | undefined>(undefined);

export const ApiKeyProvider = ({ children }: { children: React.ReactNode }) => {
  const [apiKey, setApiKeyState] = useState<string | null>(null);
  const [initialized, setInitialized] = useState(false);

  useEffect(() => {
    let key = syncApiKeyFromUrl();
    if (!key) {
      key = getStoredApiKey();
    }
    if (key) {
      setApiKeyState(key);
    }
    setInitialized(true);
  }, []);

  const setApiKey = (key: string) => {
    setStoredApiKey(key);
    setApiKeyState(key);
  };

  const clearApiKey = () => {
    clearStoredApiKey();
    setApiKeyState(null);
  };

  const value = useMemo<ApiKeyContextValue>(
    () => ({ apiKey, setApiKey, clearApiKey, initialized }),
    [apiKey, initialized]
  );

  return <ApiKeyContext.Provider value={value}>{children}</ApiKeyContext.Provider>;
};

export const useApiKeyContext = (): ApiKeyContextValue => {
  const context = useContext(ApiKeyContext);
  if (!context) {
    throw new Error("useApiKeyContext must be used within an ApiKeyProvider");
  }
  return context;
};
